import React, { useEffect, useRef } from "react";

/**
 * Sticky-scroll differentiation section.
 *
 * Left column: sticky header anchored at the top of the section.
 * Right column: each card is lg:sticky with a 48px-per-index cascade so
 * stacked cards leave a visible header strip (badge + headline edge)
 * behind the active one. An IntersectionObserver tracks each card's
 * "stack depth" — how many later cards are currently pinned on top of
 * it — and writes it to a CSS variable that drives a scale + opacity
 * transform, producing a deck-of-cards recede effect. Older cards fade
 * out once 3+ cards stack on top of them, so the visible deck stays
 * uncluttered.
 */

const STICKY_TOP_BASE = 112;
const STICKY_CASCADE = 48;

const Card = ({ badge, badgeColor, headline, body, children }) => (
  <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0d1320] p-6 md:p-8 shadow-xl shadow-black/[0.06] dark:shadow-black/40">
    <div
      className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md mb-4 border"
      style={{
        backgroundColor: `${badgeColor}14`,
        borderColor: `${badgeColor}55`,
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: badgeColor }}
      />
      <span
        className="text-[10px] font-bold uppercase tracking-widest"
        style={{ color: badgeColor }}
      >
        {badge}
      </span>
    </div>
    <h3 className="text-2xl md:text-3xl font-extrabold mb-3 leading-tight">
      {headline}
    </h3>
    <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-5">
      {body}
    </p>
    {children}
  </div>
);

const CostVisual = () => (
  <div className="space-y-4">
    <div>
      <div className="flex items-center justify-between mb-1.5 text-sm">
        <span className="text-gray-500 dark:text-gray-400">
          Traditional advisor
        </span>
        <span className="font-bold text-rose-500 tabular-nums">
          $5,000 / year
        </span>
      </div>
      <div className="h-2.5 rounded-full bg-rose-500/15 overflow-hidden">
        <div className="h-full bg-rose-500 rounded-full" style={{ width: "100%" }} />
      </div>
      <p className="text-[11px] text-gray-500 mt-1.5">
        1% of a $500K portfolio · every single year
      </p>
    </div>
    <div>
      <div className="flex items-center justify-between mb-1.5 text-sm">
        <span className="text-gray-500 dark:text-gray-400">
          FinAI · Free tier
        </span>
        <span className="font-bold text-emerald-500 tabular-nums">
          $0 / year
        </span>
      </div>
      <div className="h-2.5 rounded-full bg-emerald-500/15 overflow-hidden">
        <div
          className="h-full bg-emerald-500 rounded-full"
          style={{ width: "1%" }}
        />
      </div>
      <p className="text-[11px] text-gray-500 mt-1.5">
        Pro tier is ~$10/mo when you want more
      </p>
    </div>
  </div>
);

const SpeedVisual = () => (
  <div className="rounded-xl bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 p-4 space-y-3">
    <div className="flex justify-end">
      <div className="px-3.5 py-2 rounded-2xl rounded-br-sm bg-indigo-500 text-white text-sm max-w-[80%]">
        Should I trim NVDA?
      </div>
    </div>
    <div>
      <div className="px-3.5 py-2.5 rounded-2xl rounded-tl-sm bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-sm max-w-[88%] text-gray-700 dark:text-gray-200 leading-relaxed">
        Trim 50%. P/E at 43 is rich, 18% concentration is too high, and you'd
        lock LTCG below your exemption.
      </div>
      <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
        ⏱ Answered in 1.2s · used Lens / Vigil / Tally · live data
      </p>
    </div>
  </div>
);

const ASSETS = [
  { label: "Stocks & ETFs", icon: "📈" },
  { label: "Mutual funds", icon: "📊" },
  { label: "Real estate", icon: "🏠" },
  { label: "Gold", icon: "🥇" },
  { label: "Crypto", icon: "🪙" },
  { label: "FDs & cash", icon: "🏦" },
  { label: "Bonds", icon: "📜" },
  { label: "Business equity", icon: "💼" },
];

const CoverageVisual = () => (
  <div>
    <div className="flex flex-wrap gap-2 mb-3">
      {ASSETS.map((a) => (
        <span
          key={a.label}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-sm font-semibold"
        >
          <span>{a.icon}</span>
          <span>{a.label}</span>
        </span>
      ))}
    </div>
    <p className="text-[11px] text-gray-500 dark:text-gray-400">
      Most advisors stop at column 1. The Council reasons across all eight.
    </p>
  </div>
);

const AGENTS = [
  { name: "Anchor", tag: "orchestrator", color: "#6366f1" },
  { name: "Compass", tag: "strategy", color: "#10b981" },
  { name: "Lens", tag: "single-asset", color: "#3b82f6" },
  { name: "Vigil", tag: "risk", color: "#f43f5e" },
  { name: "Tally", tag: "tax", color: "#f59e0b" },
];

const SpecializationVisual = () => (
  <div className="grid grid-cols-5 gap-2">
    {AGENTS.map((a) => (
      <div
        key={a.name}
        className="rounded-xl border p-3 text-center"
        style={{ borderColor: `${a.color}55`, backgroundColor: `${a.color}0d` }}
      >
        <div
          className="w-9 h-9 mx-auto rounded-lg flex items-center justify-center font-extrabold text-sm mb-2"
          style={{ backgroundColor: `${a.color}33`, color: a.color }}
        >
          {a.name[0]}
        </div>
        <p className="text-xs font-bold" style={{ color: a.color }}>
          {a.name}
        </p>
        <p className="text-[10px] text-gray-500 mt-0.5">{a.tag}</p>
      </div>
    ))}
  </div>
);

const PersonalizationVisual = () => (
  <div className="rounded-xl bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 p-4">
    <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-500 mb-3">
      Your stated inputs
    </p>
    <div className="space-y-2 text-sm">
      {[
        ["Risk tolerance", "Conservative"],
        ["3-year goal", "Down-payment · $80K"],
        ["Tax jurisdiction", "India"],
        ["ESG preference", "No oil / coal / tobacco"],
      ].map(([k, v]) => (
        <div key={k} className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
          <span className="text-gray-500 dark:text-gray-400">{k}</span>
          <span className="ml-auto font-semibold text-right">{v}</span>
        </div>
      ))}
    </div>
    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-3">
      Applied to every answer — not a one-size-fits-all model portfolio.
    </p>
  </div>
);

const CARDS = [
  {
    badge: "Cost",
    badgeColor: "#10b981",
    headline: "Pay 0%, not 1% every year.",
    body:
      "A $500K portfolio sends $5,000/year to a traditional advisor most of it for things the Council does instantly. Free to start. Pay only if you outgrow it.",
    visual: <CostVisual />,
  },
  {
    badge: "Speed",
    badgeColor: "#3b82f6",
    headline: "Answers in seconds. Not quarterly reviews.",
    body:
      "Ask anytime over breakfast, during a market dip, before a trade. The Council responds in real time, with live quotes and current tax rules baked in.",
    visual: <SpeedVisual />,
  },
  {
    badge: "Coverage",
    badgeColor: "#a855f7",
    headline: "Your whole net worth not just stocks.",
    body:
      "Most advisors only see your brokerage account. The Council reasons over everything you own: equities, land, gold, crypto, FDs, bonds, even your business stake.",
    visual: <CoverageVisual />,
  },
  {
    badge: "Specialization",
    badgeColor: "#f59e0b",
    headline: "Five specialists. Not one generalist.",
    body:
      "One advisor wears every hat strategy, tax, risk, single stock and is rarely deep in all four. The Council splits the work. Each agent is laser focused. Anchor routes you to whoever (or whichever combination) actually has the answer.",
    visual: <SpecializationVisual />,
  },
  {
    badge: "Personalization",
    badgeColor: "#f43f5e",
    headline: "Grounded in your ledger. Not a model portfolio.",
    body:
      "We don't fit you to a template. Every answer reads what you actually own and your stated preferences risk, goals, jurisdiction, ESG and applies them.",
    visual: <PersonalizationVisual />,
  },
];

const WhyDifferentSection = () => {
  const cardsRef = useRef([]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    if (!mq.matches) return undefined;

    const cards = cardsRef.current.filter(Boolean);
    if (cards.length === 0) return undefined;

    // rAF-throttled scroll listener. Recomputes which cards are currently
    // pinned (i.e., sitting at their sticky-top line) every frame from
    // live DOM positions. IO-based tracking missed updates on scroll-up
    // because the IO entries didn't always re-fire when a card unpinned
    // from above — this approach is stateless so it can't drift.
    let frameId = null;

    const recalc = () => {
      frameId = null;
      const pinned = new Set();
      cards.forEach((card, i) => {
        const stickyTopPx = STICKY_TOP_BASE + i * STICKY_CASCADE;
        const rect = card.getBoundingClientRect();
        // Pinned when the card's top has reached (or passed up through)
        // its sticky line, AND the card is still tall enough below the
        // line to be visible (hasn't been pushed past the section bottom).
        if (rect.top <= stickyTopPx + 1 && rect.bottom > stickyTopPx) {
          pinned.add(i);
        }
      });
      cards.forEach((card, i) => {
        let depth = 0;
        pinned.forEach((k) => {
          if (k > i) depth++;
        });
        card.style.setProperty("--stack-depth", String(depth));
      });
    };

    const onScroll = () => {
      if (frameId === null) {
        frameId = requestAnimationFrame(recalc);
      }
    };

    recalc();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", recalc);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", recalc);
      if (frameId !== null) cancelAnimationFrame(frameId);
      cards.forEach((card) => card.style.removeProperty("--stack-depth"));
    };
  }, []);

  return (
  <section className="relative py-20 lg:py-28">
    <div className="container mx-auto px-4 md:px-8 max-w-[1500px] relative">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)] gap-10 lg:gap-20 items-start">
        {/* Sticky left — vertically centered in the viewport when pinned.
            top: 50vh moves the sticky threshold to viewport center;
            -translate-y-1/2 shifts the element up by half its own height so
            its midpoint (not its top edge) lands at the center. */}
        <div className="lg:sticky lg:top-[50vh] lg:-translate-y-1/2 lg:self-start">
          <p className="text-xs font-bold uppercase tracking-widest text-indigo-500 mb-3">
            Why FinAI
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-5 leading-[1.05]">
            Built different from a human advisor.
          </h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            A traditional advisor charges 1% AUM every year, reviews your
            portfolio quarterly, and only sees your brokerage account. The
            Council answers in seconds, costs nothing to start, and reasons
            over your whole net worth.
          </p>
        </div>

        {/* Stacking cards each pins at top with a small cascade so the
            previous card peeks out as a header strip behind the active one.
            Tight gap (32px) so no visible empty space between cards while
            still leaving the cards enough document height to scroll through. */}
        <div className="space-y-6 lg:space-y-8">
          {CARDS.map((c, i) => (
            <div
              key={c.badge}
              ref={(el) => {
                cardsRef.current[i] = el;
              }}
              className="lg:sticky"
              style={{
                top: `${(STICKY_TOP_BASE + i * STICKY_CASCADE) / 16}rem`,
                transformOrigin: "center top",
                transform: "scale(calc(1 - var(--stack-depth, 0) * 0.04))",
                opacity: "calc(1 - var(--stack-depth, 0) * 0.22)",
                transition:
                  "transform 280ms cubic-bezier(0.4, 0, 0.2, 1), opacity 280ms",
                willChange: "transform, opacity",
              }}
            >
              <Card
                badge={c.badge}
                badgeColor={c.badgeColor}
                headline={c.headline}
                body={c.body}
              >
                {c.visual}
              </Card>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
  );
};

export default WhyDifferentSection;
